import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  BadgeComponent,
  ButtonCloseDirective,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormLabelDirective,
  FormSelectDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalTitleDirective,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import {
  CardEntitlement,
  CardEntitlementDetailResponse,
  CardEntitlementService,
  CardEntitlementStats,
  CardLookupRejection
} from '../../../services/card-entitlement.service';

type Tab = 'entitlements' | 'rejections';

/**
 * Card programme admin: who is owed a card, how far the partner has got with it,
 * whether the partner has even fetched it — and who the partner asked about and got
 * nothing for.
 *
 * The two tabs answer two different questions, and the second one is the operational
 * alarm: a member on the rejection list with reason `no_entitlement` is someone we
 * owe a card and never granted.
 */
@Component({
  selector: 'app-card-entitlements-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    TableDirective,
    BadgeComponent,
    ButtonDirective,
    ButtonCloseDirective,
    FormControlDirective,
    FormLabelDirective,
    FormSelectDirective,
    ModalComponent,
    ModalHeaderComponent,
    ModalTitleDirective,
    ModalBodyComponent,
    ModalFooterComponent,
    TranslatePipe
  ],
  templateUrl: './card-entitlements-page.component.html',
  styleUrls: ['./card-entitlements-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardEntitlementsPageComponent {
  private logger = new Logger('CardEntitlementsPageComponent');
  private service = inject(CardEntitlementService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  readonly Math = Math;

  tab = signal<Tab>('entitlements');
  stats = signal<CardEntitlementStats | null>(null);

  // ── Entitlements ────────────────────────────────────────────────
  entitlements = signal<CardEntitlement[]>([]);
  loading = signal(false);
  page = signal(1);
  perPage = signal(25);
  totalCount = signal(0);
  totalPages = signal(0);

  filterStatus = signal<string>('');
  filterOrigin = signal<string>('');
  filterRequested = signal<'' | 'yes' | 'no'>('');
  filterSearch = signal<string>('');

  statusOptions = ['pending', 'in_production', 'shipped', 'delivered', 'active', 'blocked', 'expired', 'revoked'];
  originOptions = ['purchase', 'upgrade', 'grant'];

  // ── Rejections ──────────────────────────────────────────────────
  rejections = signal<CardLookupRejection[]>([]);
  rejectionsLoading = signal(false);
  rejectionsPage = signal(1);
  rejectionsTotalCount = signal(0);
  rejectionsTotalPages = signal(0);
  filterReason = signal<string>('');
  filterOpenOnly = signal(true);
  reasonOptions = ['unknown_email', 'account_closed', 'no_entitlement'];

  // ── Detail modal ────────────────────────────────────────────────
  showDetail = signal(false);
  detail = signal<CardEntitlementDetailResponse | null>(null);
  detailLoading = signal(false);

  syncingUserId = signal<number | null>(null);
  actionMessage = signal<string | null>(null);
  actionError = signal<string | null>(null);

  /** Card owners the backfill has not reached — surfaced as a warning, not a number in a list. */
  hasMissingEntitlements = computed(() => (this.stats()?.missing_entitlements ?? 0) > 0);

  constructor() {
    effect(() => {
      this.page();
      this.perPage();
      this.filterStatus();
      this.filterOrigin();
      this.filterRequested();
      this.filterSearch();
      if (this.tab() === 'entitlements') {
        this.loadEntitlements();
      }
    });

    effect(() => {
      this.rejectionsPage();
      this.filterReason();
      this.filterOpenOnly();
      if (this.tab() === 'rejections') {
        this.loadRejections();
      }
    });

    this.loadStats();
  }

  // ── Loading ─────────────────────────────────────────────────────
  private loadStats(): void {
    this.service.getStats()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: stats => this.stats.set(stats),
        error: err => this.logger.error('Failed to load card entitlement stats:', err)
      });
  }

  private loadEntitlements(): void {
    this.loading.set(true);

    this.service.getEntitlements({
      page: this.page(),
      per_page: this.perPage(),
      filter_status: this.filterStatus() || undefined,
      filter_origin: this.filterOrigin() || undefined,
      filter_requested: this.filterRequested() || undefined,
      filter_search: this.filterSearch() || undefined
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.entitlements.set(response.entitlements);
          this.totalCount.set(response.total_count);
          this.totalPages.set(response.total_pages);
          this.loading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load card entitlements:', err);
          this.loading.set(false);
        }
      });
  }

  private loadRejections(): void {
    this.rejectionsLoading.set(true);

    this.service.getRejections({
      page: this.rejectionsPage(),
      per_page: this.perPage(),
      filter_reason: this.filterReason() || undefined,
      filter_open: this.filterOpenOnly() || undefined
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.rejections.set(response.rejections);
          this.rejectionsTotalCount.set(response.total_count);
          this.rejectionsTotalPages.set(response.total_pages);
          this.rejectionsLoading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load lookup rejections:', err);
          this.rejectionsLoading.set(false);
        }
      });
  }

  // ── Actions ─────────────────────────────────────────────────────
  selectTab(tab: Tab): void {
    this.tab.set(tab);
    if (tab === 'rejections') {
      this.loadRejections();
    } else {
      this.loadEntitlements();
    }
  }

  openDetail(entitlement: CardEntitlement): void {
    this.detail.set(null);
    this.detailLoading.set(true);
    this.showDetail.set(true);

    this.service.getEntitlement(entitlement.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.detail.set(response);
          this.detailLoading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load entitlement detail:', err);
          this.detailLoading.set(false);
          this.showDetail.set(false);
        }
      });
  }

  closeDetail(): void {
    this.showDetail.set(false);
  }

  onDetailVisibleChange(visible: boolean): void {
    this.showDetail.set(visible);
  }

  /**
   * Grant the card to a member from the rejection list. Runs the same idempotent
   * generator as a purchase does — no hand-written entitlements.
   */
  grantCard(rejection: CardLookupRejection): void {
    if (!rejection.user_id) return;

    this.syncingUserId.set(rejection.user_id);
    this.actionMessage.set(null);
    this.actionError.set(null);

    this.service.syncUser(rejection.user_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.syncingUserId.set(null);
          this.actionMessage.set(
            `${response.entitlement.entitlement_id} — ${rejection.email}`
          );
          this.loadRejections();
          this.loadStats();
        },
        error: err => {
          this.syncingUserId.set(null);
          this.actionError.set(err?.error?.error || this.translate.translate('card_entitlements.sync_failed'));
          this.logger.error('Failed to grant card:', err);
        }
      });
  }

  applyFilters(): void {
    this.page.set(1);
    this.rejectionsPage.set(1);
  }

  clearFilters(): void {
    this.filterStatus.set('');
    this.filterOrigin.set('');
    this.filterRequested.set('');
    this.filterSearch.set('');
    this.page.set(1);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.page.set(page);
  }

  onRejectionsPageChange(page: number): void {
    if (page < 1 || page > this.rejectionsTotalPages()) return;
    this.rejectionsPage.set(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage.set(Number(perPage));
    this.page.set(1);
    this.rejectionsPage.set(1);
  }

  // ── Display helpers ─────────────────────────────────────────────
  statusColor(status: string): string {
    const colors: Record<string, string> = {
      pending: 'secondary',
      in_production: 'info',
      shipped: 'primary',
      delivered: 'success',
      active: 'success',
      blocked: 'warning',
      expired: 'dark',
      revoked: 'danger'
    };
    return colors[status] || 'dark';
  }

  originColor(origin: string): string {
    const colors: Record<string, string> = {
      purchase: 'success',
      upgrade: 'info',
      grant: 'warning'
    };
    return colors[origin] || 'dark';
  }

  reasonColor(reason: string): string {
    // no_entitlement is the one that means WE are missing something.
    const colors: Record<string, string> = {
      no_entitlement: 'danger',
      account_closed: 'warning',
      unknown_email: 'secondary'
    };
    return colors[reason] || 'dark';
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '–';
    return new Date(value).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDateOnly(value: string | null | undefined): string {
    if (!value) return '–';
    return new Date(value).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatAmount(value: number | null | undefined, currency: string | null | undefined): string {
    if (value === null || value === undefined) return '–';
    return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${currency || ''}`.trim();
  }
}
